'use client'

import { useState, useRef, useEffect } from 'react'
import { signIn } from 'next-auth/react'
import { motion, useInView } from 'framer-motion'
import { useRouter } from 'next/navigation'
import { apiClient } from '@/lib/api-client'
import { isPendingUser } from '@/lib/roles'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Logomark } from '@/components/brand'
import { Skeleton, SkeletonCard } from '@/components/ui/skeleton'
import { ActivityChart, DailyStats } from '@/components/ActivityChart'
import { useSessionRefresh } from '@/hooks'
import { cn } from '@/lib/utils'
import { DashboardNav, DashboardView } from '@/components/dashboard/DashboardNav'
import { GradeDistributionChart } from '@/components/dashboard/GradeDistributionChart'
import { FatalCountCard } from '@/components/dashboard/FatalCountCard'
import { AgentPerformance } from '@/components/dashboard/AgentPerformance'
import { AgentWiseView } from '@/components/dashboard/AgentWiseView'
import { WeekWiseView } from '@/components/dashboard/WeekWiseView'
import { WeekWiseKpiCard } from '@/components/dashboard/WeekWiseKpiCard'
import { CallBifurcationChart } from '@/components/dashboard/CallBifurcationChart'
import { ParametersView } from '@/components/dashboard/ParametersView'
import { PolicyViolationsView } from '@/components/dashboard/PolicyViolationsView'
import { KpiView } from '@/components/dashboard/KpiView'
import { ParetoQualityChart } from '@/components/dashboard/ParetoQualityChart'
import { FatalPriorityTable } from '@/components/dashboard/FatalPriorityTable'
import { DashboardScopeFilters } from '@/components/dashboard/DashboardScopeFilters'
import { TenureWiseOverviewCard } from '@/components/dashboard/TenureWiseOverviewCard'
import {
  ALL_PRODUCTS,
  ALL_SITES,
  appendScopeFilterParams,
  type DashboardFilterOptions,
} from '@/lib/dashboard-scope-filters'

const SHOW_GRADE_AND_PROCESSING_CARDS = false

// ============================================================================
// Types
// ============================================================================

interface RecentCall {
  id: number
  filename: string | null
  status: string
  upload_date: string | null
  overall_score: number | null
  call_outcome: string | null
  duration_seconds: number | null
}

interface TopicFrequency {
  topic: string
  count: number
  relevance: string
}

interface ParameterBifurcation {
  fatal_count: number
  non_fatal_count: number
  fatal_percentage: number
  non_fatal_percentage: number
  fatal_avg_score: number
  non_fatal_avg_score: number
}

interface AgentPerformance {
  agent_name: string
  call_count: number
  average_score: number
  grade: 'A' | 'B' | 'C' | 'D'
  total_fatal_issues: number
}

interface WeeklyStats {
  week_number: number
  call_count: number
  average_score: number
  grade_distribution: {
    grade_a: number
    grade_b: number
    grade_c: number
    grade_d: number
    grade_a_percentage: number
    grade_b_percentage: number
    grade_c_percentage: number
    grade_d_percentage: number
  }
  fatal_count: number
}

interface DashboardStats {
  total_calls: number
  completed_calls: number
  processing_calls: number
  failed_calls: number
  average_score: number
  highest_score: number
  lowest_score: number
  score_trend: number
  calls_trend: number
  daily_stats: DailyStats[]
  recent_calls: RecentCall[]
  average_talk_ratio: number | null
  positive_outcomes: number
  negative_outcomes: number
  top_topics: TopicFrequency[]
  fatal_count: number
  grade_distribution: {
    grade_a: number
    grade_b: number
    grade_c: number
    grade_d: number
    grade_a_percentage: number
    grade_b_percentage: number
    grade_c_percentage: number
    grade_d_percentage: number
  }
  parameter_bifurcation: ParameterBifurcation
  top_agents: AgentPerformance[]
  weekly_stats: WeeklyStats[]
}

// ============================================================================
// Animation variants
// ============================================================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
}

// ============================================================================
// Animated number component
// ============================================================================

function AnimatedNumber({
  value,
  suffix = '',
  duration = 1000,
}: {
  value: number
  suffix?: string
  duration?: number
}) {
  const [displayValue, setDisplayValue] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, amount: 0.5 })
  const previousValueRef = useRef<number>(0)

  useEffect(() => {
    if (!isInView) return
    
    // If value hasn't changed, don't re-animate
    if (previousValueRef.current === value) return
    
    const startValue = previousValueRef.current
    previousValueRef.current = value
    
    const startTime = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(2, -10 * progress)

      // Animate from previous value to new value
      const newDisplayValue = Math.round(startValue + eased * (value - startValue))
      setDisplayValue(newDisplayValue)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setDisplayValue(value)
      }
    }

    requestAnimationFrame(animate)
  }, [value, duration, isInView])

  const formatValue = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  return (
    <span ref={ref}>
      {formatValue(displayValue)}
      {suffix}
    </span>
  )
}

// ============================================================================
// Stats Card Component with Bento styling
// ============================================================================

interface StatCardProps {
  title: string
  value: number
  suffix?: string
  icon: React.ElementType
  iconClassName?: string
  trend?: { value: string; positive: boolean }
  colorClass: string
  delay?: number
  onClick?: () => void
}

function StatCard({
  title,
  value,
  suffix = '',
  icon: Icon,
  iconClassName,
  trend,
  colorClass,
  delay = 0,
  onClick,
}: StatCardProps) {
  return (
    <motion.div
      variants={itemVariants}
      initial='hidden'
      animate='visible'
      transition={{ delay }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={onClick ? 'cursor-pointer' : ''}
    >
      <Card className='group relative h-full overflow-hidden'>
        <CardWatermark opacity={3} scale={0.9} />
        <CardContent className='relative z-10 p-5'>
          <div className='flex items-start justify-between'>
            <div className='space-y-2'>
              <p className='text-micro uppercase text-brand-muted transition-colors duration-200 group-hover:text-brand-cornflower'>
                {title}
              </p>
              <p className='font-display text-[2.25rem] font-bold leading-none tracking-tight text-brand-navy'>
                <AnimatedNumber value={value} suffix={suffix} />
              </p>
              {trend && (
                <motion.p
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium',
                    trend.positive ? 'text-emerald-600' : 'text-red-500'
                  )}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: delay + 0.3 }}
                >
                  {trend.positive ? (
                    <Icons.trendingUp className='h-3 w-3' strokeWidth={2} />
                  ) : (
                    <Icons.trendingUp
                      className='h-3 w-3 rotate-180'
                      strokeWidth={2}
                    />
                  )}
                  {trend.value}
                </motion.p>
              )}
            </div>
            <motion.div
              className={cn(
                'rounded-xl p-2.5 text-white',
                'shadow-lg',
                colorClass
              )}
              whileHover={{ scale: 1.15, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Icon className={cn('h-5 w-5', iconClassName)} strokeWidth={1.5} />
            </motion.div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ============================================================================
// Hero Section
// ============================================================================

function HeroSection({ userName, roles }: { userName?: string; roles?: string[] }) {
  const firstName = userName?.split(' ')[0] || 'there'

  // Pick the most prominent role to display
  const ROLE_LABELS: Record<string, { label: string; color: string }> = {
    super_admin:   { label: 'Super Admin',     color: 'bg-purple-100 text-purple-700 border-purple-200' },
    superadmin:    { label: 'Super Admin',     color: 'bg-purple-100 text-purple-700 border-purple-200' },
    admin:         { label: 'Admin',           color: 'bg-blue-100 text-blue-700 border-blue-200' },
    quality_admin: { label: 'Quality Admin',   color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    quality_manager: { label: 'Quality Manager', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    Sales_analysis_admin: { label: 'Sales Admin', color: 'bg-sky-100 text-sky-700 border-sky-200' },
    supervisor:    { label: 'Supervisor',      color: 'bg-green-100 text-green-700 border-green-200' },
    manager:       { label: 'Manager',         color: 'bg-teal-100 text-teal-700 border-teal-200' },
    process_head:  { label: 'Process Head',    color: 'bg-orange-100 text-orange-700 border-orange-200' },
    hod:           { label: 'HOD',             color: 'bg-rose-100 text-rose-700 border-rose-200' },
    telecaller:    { label: 'Telecaller',      color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    user:          { label: 'User',            color: 'bg-gray-100 text-gray-600 border-gray-200' },
  }
  const ROLE_PRIORITY = [
    'super_admin', 'superadmin', 'admin', 'quality_admin', 'quality_manager',
    'Sales_analysis_admin', 'supervisor', 'manager', 'process_head', 'hod', 'telecaller', 'user',
  ]
  const displayRole = ROLE_PRIORITY.find((r) => roles?.includes(r))
  const badge = displayRole ? ROLE_LABELS[displayRole] : null

  return (
    <motion.div
      className='col-span-12 py-2'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <h1 className='text-display-3 font-bold tracking-tight text-brand-navy lg:text-display-2'>
        Every Conversation <br className='hidden sm:block' />
        <span className='text-gradient'>Analyzed & Optimized.</span>
      </h1>
      <div className='mt-4 flex items-center gap-3'>
        <p className='text-lg font-light text-muted-foreground'>
          Welcome back, {firstName}.
        </p>
        {badge && (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.color}`}>
            {badge.label}
          </span>
        )}
      </div>
    </motion.div>
  )
}

// ============================================================================
// Score Circle Component
// ============================================================================

function ScoreCircle({ score, size = 48 }: { score: number; size?: number }) {
  const circumference = 2 * Math.PI * 18
  const strokeDashoffset = circumference - (score / 100) * circumference

  const getScoreColor = (s: number) => {
    if (s >= 80) return 'text-emerald-500'
    if (s >= 60) return 'text-amber-500'
    return 'text-red-500'
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="rotate-[-90deg]"
        width={size}
        height={size}
        viewBox="0 0 48 48"
      >
        <circle
          cx="24"
          cy="24"
          r="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-gray-100"
        />
        <circle
          cx="24"
          cy="24"
          r="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          className={getScoreColor(score)}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={cn("text-sm font-bold", getScoreColor(score))}>
          {score}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Status Badge Component
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const isCompleted = status === 'completed'
  const isProcessing = status === 'processing'

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium",
        isCompleted && "bg-emerald-50 text-emerald-700",
        isProcessing && "bg-amber-50 text-amber-700",
        !isCompleted && !isProcessing && "bg-red-50 text-red-600"
      )}
    >
      {isCompleted ? (
        <Icons.checkCircle className="h-3 w-3" />
      ) : isProcessing ? (
        <Icons.loader className="h-3 w-3 animate-spin" />
      ) : (
        <Icons.alertCircle className="h-3 w-3" />
      )}
      <span className="capitalize">{status}</span>
    </div>
  )
}

// ============================================================================
// Recent Calls Card
// ============================================================================

function RecentCallsCard({ 
  calls, 
  isLoading 
}: { 
  calls: RecentCall[]
  isLoading: boolean 
}) {
  const router = useRouter()

  if (isLoading) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.messageSquare className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Recent Calls
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='space-y-3'>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className='h-16 w-full' />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='relative overflow-hidden'>
      <CardWatermark opacity={3} scale={1.1} />
      <CardHeader className='relative z-10 pb-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.messageSquare className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Recent Calls
          </CardTitle>
          <Button 
            variant='ghost' 
            size='sm' 
            onClick={() => router.push('/analyze')}
            className='text-brand-cornflower hover:text-brand-navy'
          >
            View All
            <Icons.arrowRight className='ml-1 h-4 w-4' />
          </Button>
        </div>
      </CardHeader>
      <CardContent className='relative z-10 pt-0'>
        {calls.length === 0 ? (
          <motion.div
            className='flex flex-col items-center justify-center py-12 text-center'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 mb-3'>
              <Icons.inbox className='h-7 w-7 text-gray-400' />
            </div>
            <h3 className='font-semibold text-brand-navy'>No calls yet</h3>
            <p className='mt-1 text-sm text-muted-foreground max-w-xs'>
              Upload your first sales call to get AI-powered insights
            </p>
            <Button 
              variant='gradient' 
              className='mt-4'
              onClick={() => router.push('/analyze')}
            >
              <Icons.upload className='mr-2 h-4 w-4' />
              Upload Call
            </Button>
          </motion.div>
        ) : (
          <div className='space-y-2'>
            {calls.map((call, index) => (
              <motion.div
                key={call.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => router.push(`/analyze/${call.id}`)}
                className='group flex items-center justify-between gap-3 p-3 rounded-xl border border-transparent hover:border-brand-cornflower/20 hover:bg-brand-cornflower/5 cursor-pointer transition-all'
              >
                <div className='flex items-center gap-3 min-w-0'>
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                    call.status === 'completed'
                      ? "bg-gradient-to-br from-brand-navy to-brand-purple"
                      : call.status === 'processing'
                        ? "bg-gradient-to-br from-amber-400 to-amber-500"
                        : "bg-gradient-to-br from-red-400 to-red-500"
                  )}>
                    <Icons.messageSquare className="h-4 w-4 text-white" strokeWidth={1.5} />
                  </div>
                  <div className='min-w-0'>
                    <p className='font-medium text-brand-navy truncate text-sm'>
                      {call.filename || 'Untitled Call'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {call.upload_date 
                        ? new Date(call.upload_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Unknown date'
                      }
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-3 shrink-0'>
                  <StatusBadge status={call.status} />
                  {call.status === 'completed' && call.overall_score !== null && (
                    <ScoreCircle score={call.overall_score} size={40} />
                  )}
                  <Icons.chevronRight className='h-4 w-4 text-gray-300 group-hover:text-brand-cornflower transition-colors' />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Quick Insights Card
// ============================================================================

function QuickInsightsCard({
  stats,
  isLoading,
}: {
  stats: DashboardStats | null
  isLoading: boolean
}) {
  const router = useRouter()

  if (isLoading || !stats) {
    return (
      <Card className='relative overflow-hidden h-full'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.sparkles className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Quick Insights
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='space-y-4'>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        </CardContent>
      </Card>
    )
  }

  const hasData = stats.completed_calls > 0

  return (
    <Card className='relative overflow-hidden h-full'>
      <CardWatermark opacity={3} scale={1.1} />
      <CardHeader className='relative z-10 pb-3'>
        <CardTitle className='flex items-center gap-2'>
          <Icons.sparkles className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
          Quick Insights
        </CardTitle>
      </CardHeader>
      <CardContent className='relative z-10 pt-0 space-y-4'>
        {!hasData ? (
          <div className='flex flex-col items-center justify-center py-8 text-center'>
            <Icons.brain className='h-10 w-10 text-gray-200 mb-3' />
            <p className='text-sm text-muted-foreground'>
              Insights will appear after your first completed analysis
            </p>
          </div>
        ) : (
          <>
            {/* Outcomes Summary */}
            <div className='p-4 rounded-xl bg-gradient-to-r from-brand-cornflower/5 to-brand-purple/5 border border-brand-cornflower/10'>
              <div className='flex items-center gap-2 mb-3'>
                <Icons.barChart className='h-4 w-4 text-brand-cornflower' />
                <span className='text-sm font-semibold text-brand-navy'>Call Outcomes</span>
              </div>
              <div className='grid grid-cols-3 gap-2'>
                <div className='text-center p-2 rounded-lg bg-white/50'>
                  <p className='text-lg font-bold text-emerald-600'>{stats.positive_outcomes}</p>
                  <p className='text-xs text-muted-foreground'>Positive</p>
                </div>
                <div className='text-center p-2 rounded-lg bg-white/50'>
                  <p className='text-lg font-bold text-gray-600'>
                    {stats.completed_calls - stats.positive_outcomes - stats.negative_outcomes}
                  </p>
                  <p className='text-xs text-muted-foreground'>Neutral</p>
                </div>
                <div className='text-center p-2 rounded-lg bg-white/50'>
                  <p className='text-lg font-bold text-red-500'>{stats.negative_outcomes}</p>
                  <p className='text-xs text-muted-foreground'>Negative</p>
                </div>
              </div>
            </div>

            {/* Score Range */}
            <div className='p-4 rounded-xl bg-gray-50 border border-gray-100'>
              <div className='flex items-center gap-2 mb-3'>
                <Icons.activity className='h-4 w-4 text-brand-purple' />
                <span className='text-sm font-semibold text-brand-navy'>Score Range</span>
              </div>
              <div className='flex items-center justify-between'>
                <div className='text-center'>
                  <p className='text-sm text-muted-foreground'>Lowest</p>
                  <p className={cn(
                    'text-xl font-bold',
                    stats.lowest_score >= 80 ? 'text-emerald-600' :
                    stats.lowest_score >= 60 ? 'text-amber-600' : 'text-red-500'
                  )}>{stats.lowest_score}</p>
                </div>
                <div className='flex-1 mx-4 h-2 rounded-full bg-gradient-to-r from-red-200 via-amber-200 to-emerald-200 relative'>
                  <div 
                    className='absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-brand-navy border-2 border-white shadow'
                    style={{ left: `${stats.average_score}%` }}
                  />
                </div>
                <div className='text-center'>
                  <p className='text-sm text-muted-foreground'>Highest</p>
                  <p className={cn(
                    'text-xl font-bold',
                    stats.highest_score >= 80 ? 'text-emerald-600' :
                    stats.highest_score >= 60 ? 'text-amber-600' : 'text-red-500'
                  )}>{stats.highest_score}</p>
                </div>
              </div>
            </div>

            {/* Top Topics */}
            {stats.top_topics.length > 0 && (
              <div className='p-4 rounded-xl bg-gray-50 border border-gray-100'>
                <div className='flex items-center gap-2 mb-3'>
                  <Icons.layers className='h-4 w-4 text-brand-navy' />
                  <span className='text-sm font-semibold text-brand-navy'>Top Topics</span>
                </div>
                <div className='flex flex-wrap gap-2'>
                  {stats.top_topics.slice(0, 4).map((topic, idx) => (
                    <span 
                      key={idx}
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium',
                        topic.relevance === 'high' 
                          ? 'bg-brand-cornflower text-white' 
                          : topic.relevance === 'medium'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {topic.topic} ({topic.count})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Talk Ratio */}
            {stats.average_talk_ratio !== null && (
              <div 
                className='p-4 rounded-xl bg-brand-navy/5 border border-brand-navy/10 cursor-pointer hover:bg-brand-navy/10 transition-colors'
                onClick={() => router.push('/analyze')}
              >
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm font-semibold text-brand-navy'>Average Talk Ratio</p>
                    <p className='text-xs text-muted-foreground'>Rep speaking time vs total call duration (incl. hold / silence)</p>
                  </div>
                  <div className='text-right'>
                    <p className='text-2xl font-bold text-brand-navy'>{stats.average_talk_ratio}%</p>
                    <p className='text-xs text-muted-foreground'>
                      {stats.average_talk_ratio > 60 ? 'Consider listening more' : 'Good balance'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Good Call Library Card
// ============================================================================

function GoodCallLibraryCard({
  goodCalls,
  isLoading,
}: {
  goodCalls: GoodCall[]
  isLoading: boolean
}) {
  const router = useRouter()

  if (isLoading) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={3} scale={1.1} />
        <CardHeader className='relative z-10'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.trophy className='h-5 w-5 text-amber-500' strokeWidth={1.5} />
            Good Call Library
          </CardTitle>
        </CardHeader>
        <CardContent className='relative z-10'>
          <div className='space-y-3'>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className='h-16 w-full' />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='relative overflow-hidden border-2 border-amber-100'>
      <CardWatermark opacity={3} scale={1.1} />
      <CardHeader className='relative z-10 pb-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2'>
            <div className='h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center'>
              <Icons.trophy className='h-5 w-5 text-white' strokeWidth={1.5} />
            </div>
            <div>
              <div className='text-base font-semibold'>Good Call Library</div>
              <div className='text-xs text-muted-foreground font-normal'>
                Exemplary calls with perfect execution
              </div>
            </div>
          </CardTitle>
          <Button 
            variant='ghost' 
            size='sm' 
            onClick={() => router.push('/good-calls')}
            className='text-amber-600 hover:text-amber-700 hover:bg-amber-50'
          >
            View All
            <Icons.arrowRight className='ml-1 h-4 w-4' />
          </Button>
        </div>
      </CardHeader>
      <CardContent className='relative z-10 pt-0'>
        {goodCalls.length === 0 ? (
          <motion.div
            className='flex flex-col items-center justify-center py-8 text-center'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 mb-3'>
              <Icons.trophy className='h-7 w-7 text-amber-500' />
            </div>
            <h3 className='font-semibold text-brand-navy'>No good calls yet</h3>
            <p className='mt-1 text-sm text-muted-foreground max-w-xs'>
              Calls with perfect scores will automatically appear here
            </p>
          </motion.div>
        ) : (
          <div className='space-y-2'>
            {goodCalls.slice(0, 3).map((call, index) => (
              <motion.div
                key={call.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => router.push(`/analyze/${call.id}`)}
                className='group flex items-center justify-between gap-3 p-3 rounded-xl border-2 border-amber-100 hover:border-amber-300 hover:bg-amber-50 cursor-pointer transition-all'
              >
                <div className='flex items-center gap-3 min-w-0'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 shadow-md'>
                    <Icons.star className='h-4 w-4 text-white fill-white' strokeWidth={1.5} />
                  </div>
                  <div className='min-w-0'>
                    <p className='font-medium text-brand-navy truncate text-sm'>
                      {call.filename || 'Untitled Call'}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {call.upload_date 
                        ? new Date(call.upload_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })
                        : 'Unknown date'
                      }
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-2 shrink-0'>
                  {call.overall_score !== null && (
                    <div className='flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 border border-emerald-200'>
                      <span className='text-sm font-bold text-emerald-700'>{call.overall_score}</span>
                    </div>
                  )}
                  <Icons.chevronRight className='h-4 w-4 text-gray-300 group-hover:text-amber-600 transition-colors' />
                </div>
              </motion.div>
            ))}
            {goodCalls.length > 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className='pt-2'
              >
                <Button
                  variant='outline'
                  className='w-full border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300'
                  onClick={() => router.push('/good-calls')}
                >
                  View {goodCalls.length - 3} more exemplary call{goodCalls.length - 3 !== 1 ? 's' : ''}
                  <Icons.arrowRight className='ml-2 h-4 w-4' />
                </Button>
              </motion.div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Loading skeleton for dashboard
// ============================================================================

function DashboardSkeleton() {
  return (
    <div
      className='space-y-6'
      role='status'
      aria-live='polite'
      aria-label='Loading dashboard'
    >
      <span className='sr-only'>Loading dashboard content...</span>

      {/* Hero skeleton */}
      <div className='space-y-4 py-2'>
        <Skeleton className='h-12 w-3/4 max-w-md' />
        <Skeleton className='h-6 w-1/2 max-w-sm' />
      </div>

      {/* Stats skeleton */}
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        {[...Array(4)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>

      {/* Cards skeleton */}
      <div className='grid gap-6 lg:grid-cols-12'>
        <div className='lg:col-span-12'>
          <SkeletonCard className='h-[300px]' />
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sign-in Card
// ============================================================================

function SignInCard() {
  return (
    <div className='flex min-h-[500px] items-center justify-center'>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <Card className='relative w-full max-w-md overflow-hidden bg-white shadow-float-lg transition-shadow duration-500 hover:shadow-accent'>
          <CardWatermark opacity={4} scale={1} />
          <CardHeader className='relative z-10 space-y-4 pb-8 text-center'>
            <motion.div
              className='mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border border-black/[0.06] bg-white shadow-xl'
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                delay: 0.2,
                type: 'spring',
                stiffness: 200,
                damping: 15,
              }}
            >
              <Logomark size={56} />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <CardTitle className='text-display-5 font-bold text-brand-navy'>
                Poonawalla
              </CardTitle>
              <p className='mt-2 text-muted-foreground'>
                Command Center Access
              </p>
            </motion.div>
          </CardHeader>
          <CardContent className='relative z-10 space-y-4 px-8 pb-8'>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <Button
                onClick={() => signIn('keycloak', { callbackUrl: '/' })}
                variant='gradient'
                size='lg'
                className='group w-full py-6 text-base'
              >
                Sign In with SSO
                <Icons.arrowRight className='ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1' />
              </Button>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

// ============================================================================
// Pending Approval Card
// ============================================================================

function PendingApprovalCard({ userName }: { userName?: string }) {
  const firstName = userName?.split(' ')[0] || 'there'

  return (
    <div className='flex min-h-[500px] items-center justify-center'>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className='w-full max-w-lg'
      >
        <Card className='relative overflow-hidden bg-white shadow-float-lg'>
          <CardWatermark opacity={4} scale={1} />
          <CardHeader className='relative z-10 space-y-4 pb-6 text-center'>
            <motion.div
              className='mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-amber-500 shadow-xl'
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: 0.2,
                type: 'spring',
                stiffness: 200,
                damping: 15,
              }}
            >
              <Icons.clock className='h-10 w-10 text-white' />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <CardTitle className='text-display-5 font-bold text-brand-navy'>
                Account Pending Approval
              </CardTitle>
              <p className='mt-2 text-muted-foreground'>
                Welcome, {firstName}!
              </p>
            </motion.div>
          </CardHeader>
          <CardContent className='relative z-10 space-y-6 px-8 pb-8'>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className='space-y-4'
            >
              <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 text-center'>
                <p className='text-sm text-amber-800'>
                  Your account has been created successfully, but it requires admin approval before you can access the full Command Center.
                </p>
              </div>

              <div className='space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4'>
                <h3 className='font-medium text-gray-900'>What happens next?</h3>
                <ul className='space-y-2 text-sm text-gray-600'>
                  <li className='flex items-start gap-2'>
                    <Icons.checkCircle className='mt-0.5 h-4 w-4 text-emerald-500 shrink-0' />
                    <span>An administrator will review your registration</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <Icons.checkCircle className='mt-0.5 h-4 w-4 text-emerald-500 shrink-0' />
                    <span>You&apos;ll receive an email once approved</span>
                  </li>
                  <li className='flex items-start gap-2'>
                    <Icons.checkCircle className='mt-0.5 h-4 w-4 text-emerald-500 shrink-0' />
                    <span>Then you can access all Command Center features</span>
                  </li>
                </ul>
              </div>

              <p className='text-center text-xs text-muted-foreground'>
                Need urgent access? Contact your administrator.
              </p>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

// ============================================================================
// Main Dashboard
// ============================================================================

// Good Call type
interface GoodCall {
  id: number
  filename: string
  overall_score: number | null
  upload_date: string
}

interface EmployeeOption {
  id: number
  name: string
  level_slug: string
  level_name: string
}

export default function HomePage() {
  const { session, status } = useSessionRefresh()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [goodCalls, setGoodCalls] = useState<GoodCall[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadingGoodCalls, setLoadingGoodCalls] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeView, setActiveView] = useState<DashboardView>('overview')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [targetEmployeeId, setTargetEmployeeId] = useState<number | null>(null)
  const [selectedProduct, setSelectedProduct] = useState(ALL_PRODUCTS)
  const [selectedSite, setSelectedSite] = useState(ALL_SITES)
  const [filterOptions, setFilterOptions] = useState<DashboardFilterOptions | null>(null)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const router = useRouter()

  const isSuperAdmin = (session?.roles || []).some((r: string) =>
    ['super_admin', 'superadmin'].includes(r)
  )

  // Set default date range (last 4 weeks)
  useEffect(() => {
    const today = new Date()
    const fourWeeksAgo = new Date(today)
    fourWeeksAgo.setDate(today.getDate() - 28)
    
    setStartDate(fourWeeksAgo.toISOString().split('T')[0])
    setEndDate(today.toISOString().split('T')[0])
  }, [])

  const loadFilterOptions = async () => {
    if (!startDate || !endDate) return
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      const data = await apiClient.get<DashboardFilterOptions>(
        `/api/hierarchy/dashboard-filters?${params}`
      )
      setFilterOptions(data)
    } catch (err) {
      console.error('Failed to load dashboard filter options:', err)
    }
  }

  const loadStats = async () => {
    if (!startDate || !endDate) return
    
    try {
      setIsLoading(true)
      setError(null)
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const data = await apiClient.get<DashboardStats>(
        `/api/analysis/stats?${params}`
      )
      setStats(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard stats'
      console.error('Failed to load dashboard stats:', err)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const loadGoodCalls = async () => {
    try {
      setLoadingGoodCalls(true)
      const data = await apiClient.get<GoodCall[]>('/api/good-calls/')
      setGoodCalls(data)
    } catch (err) {
      console.error('Failed to load good calls:', err)
    } finally {
      setLoadingGoodCalls(false)
    }
  }

  useEffect(() => {
    if (status === 'authenticated' && startDate && endDate) {
      loadFilterOptions()
      loadStats()
      loadGoodCalls()
      // Refresh stats every 30 seconds
      const interval = setInterval(() => {
        loadStats()
        loadGoodCalls()
      }, 30000)
      return () => clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  // Fetch employees for super admin filter
  useEffect(() => {
    if (status === 'authenticated' && isSuperAdmin) {
      apiClient.get<{id: number; name: string; level: {slug: string; name: string} | null}[]>(
        '/api/team/employees?is_active=true'
      ).then((data) => {
        setEmployees(
          data
            .filter((e) => e.level)
            .map((e) => ({
              id: e.id,
              name: e.name,
              level_slug: e.level!.slug,
              level_name: e.level!.name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      }).catch(() => {/* non-fatal */})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isSuperAdmin])

  const handleDateFilter = () => {
    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert('Start date must be before end date')
      return
    }
    loadFilterOptions()
    loadStats()
    loadGoodCalls()
  }

  if (status === 'loading') {
    return <DashboardSkeleton />
  }

  if (status !== 'authenticated') {
    return <SignInCard />
  }

  // Check if user has pending role
  const roles = session?.roles || []
  const isPending = isPendingUser(roles)

  if (isPending) {
    return <PendingApprovalCard userName={session?.user?.name || undefined} />
  }

  // Calculate trends for display
  const scoreTrendDisplay = stats?.score_trend 
    ? { value: `${stats.score_trend > 0 ? '+' : ''}${stats.score_trend}%`, positive: stats.score_trend >= 0 }
    : undefined
  
  const callsTrendDisplay = stats?.calls_trend
    ? { value: `${stats.calls_trend > 0 ? '+' : ''}${stats.calls_trend}%`, positive: stats.calls_trend >= 0 }
    : undefined

  return (
    <motion.div
      className='space-y-6'
      variants={containerVariants}
      initial='hidden'
      animate='visible'
    >
      {/* Hero Section */}
      <HeroSection userName={session?.user?.name || undefined} roles={session?.roles || []} />

      {/* Error Banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className='rounded-xl border border-red-200 bg-red-50 p-4 flex items-center justify-between'
        >
          <div className='flex items-center gap-3'>
            <Icons.alertCircle className='h-5 w-5 text-red-500' />
            <p className='text-sm text-red-700'>{error}</p>
          </div>
          <Button variant='ghost' size='sm' onClick={loadStats} className='text-red-600 hover:text-red-700'>
            <Icons.loader className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
            Retry
          </Button>
        </motion.div>
      )}

      {/* Date Range Filter */}
      <motion.div variants={itemVariants}>
        <Card className='relative overflow-hidden'>
          <CardWatermark opacity={2} />
          <CardContent className='relative z-10 p-4'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-end'>
              <div className='flex-1 space-y-2'>
                <label className='text-xs font-medium text-brand-muted'>
                  Start Date
                </label>
                <input
                  type='date'
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-cornflower focus:outline-none focus:ring-2 focus:ring-brand-cornflower/20'
                />
              </div>
              <div className='flex-1 space-y-2'>
                <label className='text-xs font-medium text-brand-muted'>
                  End Date
                </label>
                <input
                  type='date'
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-cornflower focus:outline-none focus:ring-2 focus:ring-brand-cornflower/20'
                />
              </div>
              {isSuperAdmin && (
                <div className='flex-1 space-y-2'>
                  <label className='text-xs font-medium text-brand-muted'>
                    View As (Employee)
                  </label>
                  <select
                    value={targetEmployeeId ?? ''}
                    onChange={(e) => setTargetEmployeeId(e.target.value ? Number(e.target.value) : null)}
                    className='w-full rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm focus:border-brand-cornflower focus:outline-none focus:ring-2 focus:ring-brand-cornflower/20'
                  >
                    <option value=''>All (global view)</option>
                    {Array.from(new Set(employees.map((e) => e.level_name))).sort().map((levelName) => (
                      <optgroup key={levelName} label={levelName}>
                        {employees
                          .filter((e) => e.level_name === levelName)
                          .map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}
              <Button
                onClick={handleDateFilter}
                variant='gradient'
                className='whitespace-nowrap'
              >
                <Icons.search className='mr-2 h-4 w-4' />
                Apply Filter
              </Button>
            </div>
            {isSuperAdmin && targetEmployeeId && (
              <div className='mt-2 flex items-center gap-2'>
                <span className='text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5'>
                  Viewing as: {employees.find((e) => e.id === targetEmployeeId)?.name || 'Unknown'}
                </span>
                <button
                  onClick={() => setTargetEmployeeId(null)}
                  className='text-xs text-gray-400 hover:text-gray-600'
                >
                  ✕ Clear
                </button>
              </div>
            )}
            <DashboardScopeFilters
              filterContext={filterOptions?.filter_context}
              products={filterOptions?.available_products ?? []}
              sites={filterOptions?.available_sites ?? []}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
              onProductChange={setSelectedProduct}
              onSiteChange={setSelectedSite}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Dashboard Navigation */}
      <motion.div variants={itemVariants}>
        <DashboardNav activeView={activeView} onViewChange={setActiveView} />
      </motion.div>

      {/* Conditional View Rendering */}
      {activeView === 'overview' && (
        <>
          {/* Enhanced Stats Grid */}
          <div
            className={cn(
              'grid grid-cols-2 gap-4',
              SHOW_GRADE_AND_PROCESSING_CARDS ? 'lg:grid-cols-5' : 'lg:grid-cols-3'
            )}
          >
            <StatCard
              title='Audit Count'
              value={stats?.total_calls || 0}
              icon={Icons.messageSquare}
              trend={callsTrendDisplay}
              colorClass='bg-brand-navy'
              delay={0.1}
              onClick={() => router.push('/analyze')}
            />
            <FatalCountCard
              count={stats?.fatal_count || 0}
              isLoading={isLoading}
              onClick={() => router.push('/analyze?filter=fatal')}
            />
            <StatCard
              title='Avg Score'
              value={Math.round(stats?.average_score || 0)}
              suffix='%'
              icon={Icons.activity}
              trend={scoreTrendDisplay}
              colorClass='bg-brand-cornflower'
              delay={0.3}
            />
            {SHOW_GRADE_AND_PROCESSING_CARDS && (
              <>
                <StatCard
                  title='Grade A'
                  value={Math.round(stats?.grade_distribution?.grade_a_percentage || 0)}
                  suffix='%'
                  icon={Icons.trophy}
                  colorClass='bg-emerald-500'
                  delay={0.4}
                />
                <StatCard
                  title='Processing'
                  value={stats?.processing_calls || 0}
                  icon={Icons.loader}
                  iconClassName={
                    (stats?.processing_calls ?? 0) > 0 ? 'animate-spin' : undefined
                  }
                  colorClass='bg-amber-500'
                  delay={0.5}
                  onClick={() => router.push('/analyze')}
                />
              </>
            )}
          </div>

          {/* Grade Distribution and Agent Performance */}
          <motion.div
            className='grid gap-6 lg:grid-cols-12'
            variants={itemVariants}
          >
            <div className='lg:col-span-7'>
              <GradeDistributionChart
                data={stats?.parameter_bifurcation || {
                  fatal_count: 0,
                  non_fatal_count: 0,
                  fatal_percentage: 0,
                  non_fatal_percentage: 0,
                  fatal_avg_score: 0,
                  non_fatal_avg_score: 0,
                }}
                qualityScore={Math.round(stats?.average_score || 0)}
                isLoading={isLoading}
              />
            </div>
            <div className='lg:col-span-5'>
              <AgentPerformance
                agents={stats?.top_agents || []}
                isLoading={isLoading}
              />
            </div>
          </motion.div>

          {/* Call Bifurcation Chart */}
          <motion.div variants={itemVariants}>
            <CallBifurcationChart
              dailyStats={stats?.daily_stats || []}
              isLoading={isLoading}
            />
          </motion.div>

          {/* Activity Chart */}
          <motion.div variants={itemVariants}>
            <ActivityChart 
              className='col-span-12'
              data={stats?.daily_stats || []}
              totalCalls={stats?.total_calls || 0}
              averageScore={Math.round(stats?.average_score || 0)}
              callsTrend={stats?.calls_trend || 0}
              isLoading={isLoading}
            />
          </motion.div>

          {/* Recent Calls and Quick Insights */}
          <motion.div
            className='grid gap-6 lg:grid-cols-12'
            variants={itemVariants}
          >
            <div className='lg:col-span-7'>
              <RecentCallsCard 
                calls={stats?.recent_calls || []} 
                isLoading={isLoading} 
              />
            </div>
            <div className='lg:col-span-5'>
              <QuickInsightsCard 
                stats={stats} 
                isLoading={isLoading} 
              />
            </div>
          </motion.div>

          {/* Good Call Library */}
          <motion.div variants={itemVariants}>
            <GoodCallLibraryCard
              goodCalls={goodCalls}
              isLoading={loadingGoodCalls}
            />
          </motion.div>

          {/* Week-wise KPI cards */}
          <motion.div variants={itemVariants}>
            <WeekWiseKpiCard
              startDate={startDate}
              endDate={endDate}
              targetEmployeeId={targetEmployeeId ?? undefined}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
            />
          </motion.div>

          {/* Weekly Analysis charts */}
          <motion.div variants={itemVariants}>
            <WeekWiseView
              startDate={startDate}
              endDate={endDate}
              targetEmployeeId={targetEmployeeId ?? undefined}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
            />
          </motion.div>

          {/* Tenure Wise Score — overall */}
          <motion.div variants={itemVariants}>
            <TenureWiseOverviewCard
              startDate={startDate}
              endDate={endDate}
              targetEmployeeId={targetEmployeeId ?? undefined}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
            />
          </motion.div>

          {/* Pareto Analysis — Quality Parameters */}
          <motion.div variants={itemVariants}>
            <ParetoQualityChart
              startDate={startDate}
              endDate={endDate}
              targetEmployeeId={targetEmployeeId ?? undefined}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
            />
          </motion.div>

          {/* Fatal Priority Impact Table */}
          <motion.div variants={itemVariants}>
            <FatalPriorityTable
              startDate={startDate}
              endDate={endDate}
              targetEmployeeId={targetEmployeeId ?? undefined}
              selectedProduct={selectedProduct}
              selectedSite={selectedSite}
            />
          </motion.div>
        </>
      )}

      {activeView === 'agent-wise' && (
        <motion.div variants={itemVariants}>
          <AgentWiseView
            agents={stats?.top_agents || []}
            isLoading={isLoading}
          />
        </motion.div>
      )}

      {activeView === 'parameters' && (
        <motion.div variants={itemVariants}>
          <ParametersView 
            startDate={startDate} 
            endDate={endDate} 
            isLoading={isLoading}
            targetEmployeeId={targetEmployeeId ?? undefined}
            selectedProduct={selectedProduct}
            selectedSite={selectedSite}
          />
        </motion.div>
      )}

      {activeView === 'policy-violations' && (
        <motion.div variants={itemVariants}>
          <PolicyViolationsView
            startDate={startDate}
            endDate={endDate}
            isLoading={isLoading}
            targetEmployeeId={targetEmployeeId ?? undefined}
            selectedProduct={selectedProduct}
            selectedSite={selectedSite}
          />
        </motion.div>
      )}

      {activeView === 'kpi' && (
        <motion.div variants={itemVariants}>
          <KpiView startDate={startDate} endDate={endDate} targetEmployeeId={targetEmployeeId ?? undefined} />
        </motion.div>
      )}
    </motion.div>
  )
}
